<template>
  <div v-if="show">

    <loading :loading="loading"></loading>

    <error-alert-item
        :error-alert-event="errorAlertEvent"
        :network-error="networkError"
        @resetErrorAlertEvent="errorAlertEvent=false"
    ></error-alert-item>

    <div v-if="bankDetailsHeading || bankDetailsDescription"
         class="item-heading-desc-div margin-left-10 margin-right-10">
      <weex-text
          class="item-heading"
          v-html="bankDetailsHeading"
      ></weex-text>
      <weex-text
          class="item-heading"
          v-html="bankDetailsDescription"
      ></weex-text>
    </div>

    <div class="margin-left-10 margin-right-10">
      <div class="input_container" v-if="isFieldNeeded('account_holder_name')">
        <weex-text class="input_label">{{ getFieldLabel('account_holder_name') }}</weex-text>
        <input
            class="input_with_border_bottom"
            :value="getFieldValue('account_holder_name')"
            @input="onInputEntered('account_holder_name', $event.target.value)"
            return-key-type="next">
        <field-error-item
            :show-error="showFieldError('account_holder_name')"
            :error-msg="getErrorMsg('account_holder_name')"
        ></field-error-item>
      </div>

      <div class="input_container" v-if="isFieldNeeded('account_number')">
        <weex-text class="input_label">{{ getFieldLabel('account_number') }}</weex-text>
        <input
            class="input_with_border_bottom"
            :value="getFieldValue('account_number')"
            @input="onInputEntered('account_number', $event.target.value)"
            type="number"
            return-key-type="next">
        <field-error-item
            :show-error="showFieldError('account_number')"
            :error-msg="getErrorMsg('account_number')"
        ></field-error-item>
      </div>

      <div class="dropdown_container" v-show="isFieldNeeded('account_type')">
        <wxc-dropdown
            default-item="Select"
            :title="getFieldLabel('account_type')"
            :select-item="getFieldValue('account_type')"
            :config="dropdownConfig"
            :list="accountTypes"
            @onItemSelected="onInputEntered('account_type', $event.value)"
        ></wxc-dropdown>
        <field-error-item
            :show-error="showFieldError('account_type')"
            :error-msg="getErrorMsg('account_type')"
        ></field-error-item>
      </div>

      <div class="input_container" v-if="isFieldNeeded('opening_year')">
        <weex-text class="input_label">{{ getFieldLabel('opening_year') }}</weex-text>
        <input
            class="input_with_border_bottom"
            :value="getFieldValue('opening_year')"
            :placeholder="openingYearPlaceHolder"
            @input="onInputEntered('opening_year', $event.target.value)"
            type="number"
            return-key-type="next">
        <field-error-item
            :show-error="showFieldError('opening_year')"
            :error-msg="getErrorMsg('opening_year')"
        ></field-error-item>
      </div>

      <div class="dropdown_container" v-show="isFieldNeeded('banking')">
        <wxc-dropdown
            default-item="Select"
            :title="getFieldLabel('banking')"
            :select-item="getFieldValue('banking')"
            :config="dropdownConfig"
            :list="bankingTypes"
            @onItemSelected="onInputEntered('banking', $event.value)"
        ></wxc-dropdown>
        <field-error-item
            :show-error="showFieldError('banking')"
            :error-msg="getErrorMsg('banking')"
        ></field-error-item>
      </div>

      <div class="input_container" v-if="isFieldNeeded('years_held')">
        <weex-text class="input_label">{{ getFieldLabel('years_held') }}</weex-text>
        <input
            class="input_with_border_bottom"
            :value="getFieldValue('years_held')"
            @input="onInputEntered('years_held', $event.target.value)"
            type="number"
            maxlength="2"
            return-key-type="next">
        <field-error-item
            :show-error="showFieldError('years_held')"
            :error-msg="getErrorMsg('years_held')"
        ></field-error-item>
      </div>

      <div class="input_container" v-show="isFieldNeeded('ifsc_code')">
        <weex-text class="input_label">{{ getFieldLabel('ifsc_code') }}</weex-text>
        <div class="item-row-alignment-justify-content">
          <input
              class="item-ifsc-code-first-part"
              maxlength="4"
              :value="ifscFirstPart"
              @input="ifscFirstPartEntered($event.target.value)"
              return-key-type="next">
          <weex-text class="item-ifsc-code-dash-part">&#8212;</weex-text>
          <input
              ref="ifscSecondPartField"
              class="item-ifsc-code-second-part"
              maxlength="7"
              :value="ifscSecondPart"
              @input="ifscSecondPartEntered($event.target.value)"
              return-key-type="next">
        </div>
        <field-error-item
            :show-error="showFieldError('ifsc_code')"
            :error-msg="getErrorMsg('ifsc_code')"
        ></field-error-item>
      </div>

      <div class="input_container" v-if="isFieldNeeded('bank_name')">
        <weex-text class="input_label">{{ getFieldLabel('bank_name') }}</weex-text>
        <input
            class="input_with_border_bottom"
            :value="getFieldValue('bank_name')"
            @input="onInputEntered('bank_name', $event.target.value)"
            auto-capitalization-type="allCharacters"
            :disabled="disableBankName"
            return-key-type="next">

        <field-error-item
            :show-error="showFieldError('bank_name')"
            :error-msg="getErrorMsg('bank_name')"
        ></field-error-item>
      </div>

      <div class="input_container" v-if="showBranchName">
        <weex-text class="input_label">{{ getFieldLabel('branch_name') }}</weex-text>
        <input
            class="input_with_border_bottom"
            :value="getFieldValue('branch_name')"
            :disabled="true"
            return-key-type="next">
      </div>

    </div>

    <wxc-button
        text="Next"
        class="button_horizontal_center margin_top_40 margin-bottom-20"
        type="blue"
        @wxcButtonClicked="onSubmit"
    ></wxc-button>

  </div>
</template>

<script>

import Loading from "@/components/layout/Loading";
import ErrorAlertItem from "@/components/layout/ErrorAlertItem";
import DynamicFieldUtils, {FieldTypes} from "@/utils/DynamicFieldUtils";
import EzUtils from "@/utils/EzUtils";
import {ValidationPatterns} from "@/utils/ValidationPatterns";
import DataSource from "@/data/DataSource";
import {DataTables} from "@/utils/DataTables";
import LoanScopeDataHolder from "@/data/model/LoanScopeDataHolder";
import WxcButton from "../../../weex-ui/packages/wxc-button/index";
import WxcDropdown from "../../../weex-ui/packages/wxc-dropdown/index";
import {DefaultResourceConfig} from "@/utils/ImageResourcesUtil";
import FieldErrorItem from "@/components/layout/FieldErrorItem";

const ALL_FIELDS_DATA = {
  account_holder_name: {
    key: "account_holder_name",
    label: "Account Holder Name",
    validation: "",
    fieldType: FieldTypes.EDIT_FIELD,
    errorMsg: "Please enter Account Holder Name.",
    optional: false
  },

  account_number: {
    key: "account_number",
    label: "Account Number",
    validation: ValidationPatterns.NUMBER_PATTERN,
    fieldType: FieldTypes.EDIT_FIELD,
    errorMsg: "Please enter valid Account Number.",
    optional: false
  },

  account_type: {
    key: "account_type",
    label: "Account Type",
    validation: "",
    fieldType: FieldTypes.DROPDOWN,
    errorMsg: "Please select Account Type.",
    optional: false
  },

  opening_year: {
    key: "opening_year",
    label: "Account Opening Year",
    validation: "^([1][9]|[2][0])[0-9]{2}$",
    fieldType: FieldTypes.EDIT_FIELD,
    errorMsg: "Please enter valid Account Opening Year.",
    optional: false
  },

  banking: {
    key: "banking",
    label: "Banking Type",
    validation: "",
    fieldType: FieldTypes.DROPDOWN,
    errorMsg: "Please select Banking Type.",
    optional: false
  },

  years_held: {
    key: "years_held",
    label: "Years Held",
    validation: ValidationPatterns.YEARS_PATTERN,
    fieldType: FieldTypes.EDIT_FIELD,
    errorMsg: "Please enter valid no of Years.",
    optional: false
  },

  ifsc_code: {
    key: "ifsc_code",
    label: "IFSC Code",
    validation: ValidationPatterns.IFSC_PATTERN,
    fieldType: FieldTypes.EDIT_FIELD,
    errorMsg: "Please enter valid IFSC Code.",
    optional: false
  },

  bank_name: {
    key: "bank_name",
    label: "Bank Name",
    validation: "",
    fieldType: FieldTypes.EDIT_FIELD,
    errorMsg: "Please enter Bank Name.",
    optional: false
  },

  branch_name: {
    key: "branch_name",
    label: "Branch Name",
    validation: "",
    fieldType: FieldTypes.EDIT_FIELD,
    optional: true
  }
};

export default {
  components: {Loading, ErrorAlertItem, WxcButton, WxcDropdown, FieldErrorItem},
  props: {
    show: {
      type: Boolean,
      default: false,
      required: true
    },

    bankDetailsHeading: {
      type: String,
      default: ""
    },

    bankDetailsDescription: {
      type: String,
      default: ""
    }
  },

  data: () => ({
    accountTypes: [],
    bankingTypes: [
      {title: "Primary", value: "Primary"},
      {title: "Additional", value: "Additional"}
    ],
    bankDetails: {
      account_holder_name: null,
      account_number: null,
      account_type: null,
      opening_year: null,
      banking: null,
      years_held: null,
      ifsc_code: null,
      bank_name: null,
      branch_name: null
    },
    disableBankName: false,
    showBranchName: false,
    loading: false,
    errorAlertEvent: false,
    networkError: {},
    showUI: false,
    dynamicFields: null,
    invalidFields: [],
    ifscFirstPart: "",
    ifscSecondPart: ""
  }),

  methods: {

    showBankDetailsFields(accountTypes, bankDetails) {
      accountTypes && accountTypes.forEach(accountType => {
        if (accountType) {
          this.accountTypes.push({title: accountType, value: accountType});
        }
      });

      if (bankDetails) {
        this.onInputEntered(
            ALL_FIELDS_DATA.account_holder_name.key,
            bankDetails[ALL_FIELDS_DATA.account_holder_name.key]
        );
        this.onInputEntered(
            ALL_FIELDS_DATA.account_number.key,
            bankDetails[ALL_FIELDS_DATA.account_number.key]
        );
        this.onInputEntered(
            ALL_FIELDS_DATA.account_type.key,
            bankDetails[ALL_FIELDS_DATA.account_type.key]
        );
        this.onInputEntered(
            ALL_FIELDS_DATA.opening_year.key,
            bankDetails[ALL_FIELDS_DATA.opening_year.key]
        );
        this.onInputEntered(
            ALL_FIELDS_DATA.banking.key,
            bankDetails[ALL_FIELDS_DATA.banking.key]
        );
        this.onInputEntered(
            ALL_FIELDS_DATA.years_held.key,
            bankDetails[ALL_FIELDS_DATA.years_held.key]
        );
        this.onInputEntered(
            ALL_FIELDS_DATA.bank_name.key,
            bankDetails[ALL_FIELDS_DATA.bank_name.key]
        );
        this.onInputEntered(
            ALL_FIELDS_DATA.branch_name.key,
            bankDetails[ALL_FIELDS_DATA.branch_name.key]
        );
        let ifsc = bankDetails[ALL_FIELDS_DATA.ifsc_code.key];
        if (EzUtils.validateIFSC(ifsc)) {
          ifsc = ifsc.toUpperCase();
          this.ifscFirstPart = ifsc.substring(0, 4);
          this.ifscSecondPart = ifsc.substring(4);
          this.ifscEntered(ifsc);
        }
      }

      if (!this.getFieldValue(ALL_FIELDS_DATA.account_holder_name.key)) {
        this.onInputEntered(
            ALL_FIELDS_DATA.account_holder_name.key,
            LoanScopeDataHolder.getName()
        );
      }

      if (!this.getFieldValue(ALL_FIELDS_DATA.account_type.key)) {
        const accountType = this.accountTypes.length > 0
            ? this.accountTypes[0].value
            : "";
        this.onInputEntered(ALL_FIELDS_DATA.account_type.key, accountType);
      }
    },

    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    isFieldNeeded(key) {
      return !this.dynamicFields || this.dynamicFields[key];
    },

    getFieldLabel(key) {
      if (this.dynamicFields && this.dynamicFields[key] && this.dynamicFields[key]["label"]) {
        return this.dynamicFields[key]["label"];
      }
      return ALL_FIELDS_DATA[key]["label"];
    },

    getFieldValue(key) {
      return this.bankDetails ? this.bankDetails[key] : "";
    },

    showFieldError(fieldKey) {
      return this.invalidFields && this.invalidFields.includes(fieldKey);
    },

    getErrorMsg(fieldKey) {
      return ALL_FIELDS_DATA[fieldKey]["errorMsg"];
    },

    onInputEntered(key, value) {
      this.bankDetails[key] = value;
      this.removeFromInvalidFieldsIfValid(key);
    },

    removeFromInvalidFieldsIfValid(key) {
      this.invalidFields = DynamicFieldUtils.removeFromInvalidFieldsIfValidValue(
          this.invalidFields,
          key,
          this.bankDetails[key],
          ALL_FIELDS_DATA,
          this.dynamicFields
      );
    },

    ifscEntered(ifsc) {
      this.onInputEntered(ALL_FIELDS_DATA.ifsc_code.key, ifsc);
      this.getIFSCcDetailsIfValidIFSC();
    },

    ifscFirstPartEntered(ifscFirstPart) {
      this.ifscFirstPart = ifscFirstPart ? ifscFirstPart.toUpperCase() : ifscFirstPart;
      this.ifscEntered(this.ifscFirstPart + this.ifscSecondPart);
      if (EzUtils.doesPatternMatch(
          ValidationPatterns.IFSC_FIRST_PART_PATTERN,
          this.ifscFirstPart
      )) {
        this.$refs.ifscSecondPartField.focus();
      }
    },

    ifscSecondPartEntered(ifscSecondPart) {
      this.ifscSecondPart = ifscSecondPart ? ifscSecondPart.toUpperCase() : ifscSecondPart;
      this.ifscEntered(this.ifscFirstPart + this.ifscSecondPart);
    },

    getIFSCcDetailsIfValidIFSC() {
      if (EzUtils.validateIFSC(this.getFieldValue(ALL_FIELDS_DATA.ifsc_code.key))) {
        this.loading = true;
        DataSource.getBankIFSCDetails(this.getFieldValue(ALL_FIELDS_DATA.ifsc_code.key))
        .then(ifscDetailsRes => {
          this.loading = false;
          if (!ifscDetailsRes.errorOccurred && ifscDetailsRes.data) {
            const ifscDetails = ifscDetailsRes.data;
            if (ifscDetails.BANK) {
              this.onInputEntered(ALL_FIELDS_DATA.bank_name.key, ifscDetails.BANK);
              this.disableBankName = true;
            } else {
              this.disableBankName = false;
            }

            if (ifscDetails.BRANCH) {
              this.onInputEntered(ALL_FIELDS_DATA.branch_name.key, ifscDetails.BRANCH);
              this.showBranchName = true;
            } else {
              this.showBranchName = false;
            }
          } else {
            this.disableBankName = false;
            this.showBranchName = false;
          }
        })
      } else {
        this.disableBankName = false;
        this.showBranchName = false;
      }
    },

    onSubmit() {
      if (this.checkForValidation()) {
        this.loading = true;
        DataSource.getDataTableInfo(DataTables.BANK_DETAILS, LoanScopeDataHolder.getClientId())
        .then(bankDetailsRes => {
          if (bankDetailsRes.errorOccurred) {
            this.showError(bankDetailsRes.data);
          } else {
            const bankDetailsList = bankDetailsRes.data;
            if (EzUtils.isNotEmpty(bankDetailsList)) {
              DataSource.updateDataTableEntryInMultiRowDataTable(
                  DataTables.BANK_DETAILS,
                  LoanScopeDataHolder.getClientId(),
                  bankDetailsList[bankDetailsList.length - 1].id,
                  this.bankDetails
              )
              .then(res => {
                this.loading = false;
                if (res.errorOccurred) {
                  this.showError(res.data);
                } else {
                  this.onBankDetailsSavedOnUpdated();
                }
              })
            } else {
              DataSource.addDataTableEntry(
                  DataTables.BANK_DETAILS,
                  LoanScopeDataHolder.getClientId(),
                  this.bankDetails
              )
              .then(res => {
                this.loading = false;
                if (res.errorOccurred) {
                  this.showError(res.data);
                } else {
                  this.onBankDetailsSavedOnUpdated();
                }
              })
            }
          }
        })
      }
    },

    onBankDetailsSavedOnUpdated() {
      this.$emit("onBankDetailsAdded");
    },

    checkForValidation() {
      this.invalidFields = DynamicFieldUtils.validateDynamicFieldsForRequiredFields(
          this.bankDetails,
          ALL_FIELDS_DATA,
          this.dynamicFields
      );

      return !this.invalidFields || this.invalidFields.length === 0;
    },

  },

  created() {
    this.dynamicFields = DynamicFieldUtils.getRequiredDynamicFieldsData();
  },

  computed: {

    dropdownConfig() {
      return DefaultResourceConfig.DROP_DOWN;
    },

    openingYearPlaceHolder() {
      return this.getFieldValue(ALL_FIELDS_DATA.opening_year.key) ? "" : "YYYY";
    }
  }
};
</script>

<style
    scoped
    src="../../assets/style/base.css"></style>
